import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import {
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import {
  ButtonComponent,
  IconComponent,
  IToastEvent,
  ToastComponent,
} from '@sarasanalytics-com/design-system';
import { Token } from 'src/app/app.model';
import { IAppParams, IUserInfo, Products } from 'src/app/interfaces/state-interface';
import { IToast } from 'src/app/interfaces/toast-interface';
import { AppService } from 'src/app/services/app.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { CookiesService } from 'src/app/services/cookies/cookies.service';
import { DataService } from 'src/app/services/data/data.service';
import { QueryParamService } from 'src/app/services/queryParam/query-param.service';
import { RedirectionService } from 'src/app/services/redirection/redirection.service';
import { SignupService } from 'src/app/services/signup/signup.service';
import { StoreService } from 'src/app/services/store/store.service';
import {
  IMessageView,
  MessageViewComponent,
  MessageViewEvent,
} from 'src/app/shared/components/message-view/message-view.component';
import { formLinks, password, setupEmail, setupName, signupFirstNameProps, signupLastNameProps } from 'src/app/static/form';
import {
  emailDoesNotExists,
  SignupEmailVerificationFailure,
  SignupSuccess,
  verifyEmailExpired,
  verifyPageFailure,
} from 'src/app/static/message-view';
import { samePasswordValidator } from 'src/app/validators/validators';
import { ProductDetailService } from 'src/app/services/product/productdetails.service';

@Component({
  selector: 'app-verify',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FormlyModule,
    ToastComponent,
    ButtonComponent,
    MessageViewComponent,
    IconComponent,
  ],
  templateUrl: './verify.component.html',
  styleUrl: './verify.component.scss',
})
export class VerifyComponent {
  private route: ActivatedRoute = inject(ActivatedRoute);
  private router: Router = inject(Router);
  private store: StoreService = inject(StoreService);
  private signupService: SignupService = inject(SignupService);
  private afAuth: AngularFireAuth = inject(AngularFireAuth);
  private redirectionService: RedirectionService = inject(RedirectionService);
  private cookieService: CookiesService = inject(CookiesService);
  private authService: AuthService = inject(AuthService);
  private appService: AppService = inject(AppService);
  private productService: ProductDetailService = inject(ProductDetailService);

  userData: any;

  toast: any; // = successToast;
  form: FormGroup;

  queryParams: IAppParams;

  messageView: IMessageView | null;

  userId: string;

  userInfo: IUserInfo = {};
  product: Products;
  code: string;

  model: any = {};
  fields: FormlyFieldConfig[];

  isLoading: boolean = false;
  showInitialLoader: boolean = true;

  privacyPolicy: string = '';
  termsOfUse: string = '';
  product_logo: string = '';

  ngOnInit() {
    this.privacyPolicy = formLinks.privacyPolicy;
    this.termsOfUse = formLinks.termsOfUse;
    this.form = new FormGroup({});

    setTimeout(() => {
      const qp = this.route.snapshot.queryParamMap;
      this.product = (
        qp.get('productName') ||
        qp.get('productType') ||
        Products.SARAS
      ).toUpperCase() as Products;
      this.userInfo.email = this.appService.sanitizeEmailFromParams(
        qp.get('email') || ''
      );
      this.userInfo.firstName = qp.get('fullName') || '';
      this.userInfo.userId = qp.get('id') || '';
      this.code = qp.get('code') || '';
      this.userId = this.userInfo.userId || this.userId;
      this.checkLinkExpiry(this.userId, this.code);
      this.product_logo = this.productService.getProductLogo(this.product);
    });
  }

  checkLinkExpiry(userId: string, code: string){
    this.showInitialLoader = true;
    try{
      if(this.userInfo?.email){
        this.signupService.isEmailVerified(this.userInfo.email).subscribe({
          next: (response) => {
            if(response.success){
              if(response.data){
                /* Email Is Verified */
                this.router.navigateByUrl(formLinks.login(this.product)+'&mode=verified');
              }else{
                /* Email Is Not Verified */
                this.signupService.checkLinkExpiry(userId, code).subscribe({
                  next: (response)=>{
                    this.showInitialLoader = false;
                    if (response.success && response.data === "VALID_LINK") {
                      if(this.userId && this.code && this.userInfo?.email){
                        this.toast = successToast;
                        this.generateFields();
                      }else{
                        this.messageView = verifyPageFailure();
                      }
                    }else{
                      this.showExpiryMessageView(response.errors);
                    }
                  },
                  error: (err)=>{
                    this.showInitialLoader = false;
                    console.log(err);
                    this.showExpiryMessageView(err);
                  }
                })
              }
            }else{
              /* Email not found / Email does not exists */
              this.showInitialLoader = false;
              this.showExpiryMessageView(response?.errors);
            }
          },
          error: (err) => {
            this.showInitialLoader = false;
            console.log(err);
          }
        })
      }
    }catch(e){
      console.log(e);
    }
  }

  showExpiryMessageView(errors: any){
    if(errors?.code === 'E0004'){
      this.messageView = verifyEmailExpired();
    }else if(errors?.code === 'E0001'){
      this.messageView = emailDoesNotExists();
    }else{
      this.messageView = verifyPageFailure();
    }
  }

  generateFields(showAll: boolean = true) {
    const passwordInputs = showAll
      ? [
          {
            key: 'password',
            type: 'sa-input',
            className: 'col-12',
            props: password('Must contain minimum of 8 characters', {
              placeholder: 'Enter Password',
              validators: [Validators.minLength(8)]
            }),
          },
          {
            key: 'reenter-password',
            type: 'sa-input',
            className: 'col-12',
            props: password('', {
              placeholder: 'Confirm Password',
              validators: [samePasswordValidator('password'), Validators.minLength(8)],
            }),
          },
        ]
      : [];
    this.fields = [
      ...(!this.userInfo?.firstName?.trim()
      ? [{
          key: 'firstname',
          type: 'sa-input',
          className: 'col-12 block',
          props: signupFirstNameProps(),
        },
        {
          key: 'lastname',
          type: 'sa-input',
          className: 'col-12 block',
          props: signupLastNameProps(),
        }]
      : [{
          key: 'firstname',
          type: 'sa-input',
          className: 'col-12 block',
          props: setupName(
            true,
            `${this.userInfo?.firstName || ''} ${
              this.userInfo?.lastName || ''
            }`.trim()
          ),
        }]
      ),
      {
        key: 'email',
        type: 'sa-input',
        className: 'col-12 block',
        props: setupEmail(true, this.userInfo?.email || ''),
      },
      ...passwordInputs,
    ];
  }

  modelChangeHandler(evt: any) {
    this.form.get('reenter-password')?.updateValueAndValidity();
  }
  onSubmit() {
    try {
      if (this.form.valid && this.model.password) {
        this.isLoading = true;
        this.signupService
          .setPassword(
            this.code || '',
            this.userInfo.userId || '',
            this.model.password,
            this.model.firstname || this.userInfo.firstName || '',
            this.model.lastname || this.userInfo.lastName || ''
          )
          .subscribe({
            next: (value) => {
              if (value.success) {
                this.signinUser();
              } else {
                this.isLoading = false;
                this.handleErrors(value);
              }
            },
            error: (err) => {
              this.isLoading = false;
              this.handleErrors(err);
            },
          });
      }else{
        this.form.markAllAsTouched();
      }
    } catch (e: any) {
      console.log(e);
      this.handleErrors(e);
    }
  }

  signinUser() {
    if (this.userInfo.email) {
      this.afAuth
        .signInWithEmailAndPassword(this.userInfo.email, this.model.password)
        .then((result: any) => {
          this.userData = result.user;
          return result.user.getIdTokenResult();
        })
        .then((idTokenResult) => {
          this.signupService
            .verifyFirebaseToken(idTokenResult.token)
            .subscribe((res: any)=>{
              console.log(res, 'verify firebase token subs');
              if(res.success && res.data){
                const token: Token = res.data;
                const userId = token?.userId;

                this.authService.globalAuthInitialized = true;
  
                /* GET USER DATA */
                this.signupService.getUserInfo(userId).subscribe((userInfoRes)=>{
                  if(userInfoRes.success && userInfoRes.data){
                    this.cookieService.addSharedCookie(token);
                    this.store.setSarasPortalData('LOGIN');
                    this.redirectionService.redirectToProduct();
                  }
                })
                
              }else{
                this.handleErrors(res);
              }
            })
        })
        .catch((e) => {
          console.log(e);
          this.handleErrors(e);
        });
    }
  }

  handleErrors(response: any) {
    if (response?.code === 'E0002') {
      this.messageView = verifyEmailExpired();
    } else {
      const code: string = response?.code || 'DEFAULT';
      const errMsg = response?.code ? '' : (response?.errors?.message || '');
      /*@ts-ignore*/
      const message = errMsg || API_ERRORS.FIREBASE_MESSAGES[code] || API_ERRORS.FIREBASE_MESSAGES.DEFAULT;
      this.toast = failureToast(message);
      this.isLoading = false;
    }
  }

  sendVerificationEmail() {
    if (this.userInfo.userId) {
      this.signupService.sendVerificationEmail(this.userInfo.userId).subscribe({
        next: (response: any) => {
          if (response.success) {
            this.messageView = SignupSuccess(this.userInfo.email || '');
          } else {
            this.messageView = SignupEmailVerificationFailure(
              this.userInfo.email || ''
            );
          }
        },
      });
    }
  }

  onMessageViewEvent(evt: any) {
    switch (evt.type) {
      case MessageViewEvent.REFRESH:
        break;
      case MessageViewEvent.RESEND:
        this.sendVerificationEmail();
        break;
    }
  }
}
const successToast: IToast = {
  status: 'success',
  subHeading: 'Your email is verified! Complete your account setup below.',
};

const failureToast = (msg: string = ''): IToast => {
  return {
    status: 'failure',
    subHeading: msg || 'Something went wrong, retry after some time.'
  }
};
